import {
  ACCOUNTING_STATES,
  DOCUMENT_FAMILIES,
  DOCUMENT_NATURES,
  DOCUMENT_STATES,
} from '@croissants/domain/vocabulary';
import { z } from 'zod';
import {
  BasisPoints,
  CurrencyCode,
  DeliveryWindow,
  DoorNumber,
  GeoPoint,
  ImageUrl,
  Instant,
  Locale,
  LocalTime,
  MinorUnits,
  MinorUnitsNonNegative,
  PhoneE164,
  PublicId,
  RemoteObjectUrl,
  ServiceDay,
  TimeZone,
  Uuid,
} from './common';
import {
  AcquisitionChannel,
  AllergenCode,
  DeliveryJobStatus,
  DietaryTag,
  DoorPosition,
  FulfillmentMode,
  GuestStage,
  IncidentCause,
  ItemAvailability,
  OrderLifecycleWire,
  OrderSummaryState,
  OrganizationType,
  PayoutVerificationState,
  ProviderCapability,
  RatingSubject,
  StorefrontMode,
  SubstitutionChoice,
} from './enums';
import { GuestStageMessageKeySchema } from './messages';
import { AllowedStorefrontModes } from './projections/storefront-modes';

/** Resource shapes shared by more than one mount. Route modules compose these. */

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export const ProductImage = z.object({
  url: ImageUrl,
  alt: z.string().max(200),
  width: z.int().positive().optional(),
  height: z.int().positive().optional(),
});
export type ProductImage = z.infer<typeof ProductImage>;

/**
 * The same picture on the way IN - what a curator WRITES, which lands in
 * `catalogue_items.images`. The same schema as `ProductImage`: both directions
 * refuse the bytes (`ImageUrl`), the memory world included.
 */
export const ProductImageInput = ProductImage;

/**
 * What an item IS, and therefore which section of the vitrine it lands in: the
 * type is the section's title (CAT-016).
 *
 * Free text rather than a closed vocabulary, because a Paris counter does not
 * sell two categories of thing - it sells viennoiseries, breads, patisseries,
 * drinks - and every one of those was an "Extra" until a curator could name it.
 * The WORDS over a section are a record now, authored in both languages on the
 * internal Kinds tab (CAT-016), and the key an item carries is what that record
 * is keyed on - which is why nothing had to be renamed for the words to exist.
 *
 * Not an enum in the database either - see `catalogue_items.kind`. A menu
 * section that needs a migration to exist is a menu section nobody adds.
 */
export const ProductType = z.string().min(1).max(40);
export type ProductType = z.infer<typeof ProductType>;

/**
 * The two keys this codebase SHIPPED with, before a kind was a record.
 *
 * No privilege attaches to them and none may be reintroduced (founder,
 * 2026-08-27): every kind is editable, removable and reorderable, these two
 * included. They are named here for exactly two reasons - the registry offers
 * them on a world where nothing carries them yet, and `MENU` is the key a
 * formule carries, which is a fact about the code rather than about the words.
 */
export const SHIPPED_PRODUCT_TYPES = ['MENU', 'EXTRA'] as const;

/**
 * A server-authored string in EVERY LANGUAGE THE PLATFORM SHIPS (NFR-006).
 *
 * Stated once here and carried beside the served field, never instead of it.
 * The served field is the language of the request - the session's locale claim
 * - and stays the fallback for any reader that has none of its own and for
 * every client older than these maps. A reader with a language of its own picks
 * out of the map with `textIn` (`@croissants/contracts/projections`).
 *
 * This is what makes the guest's language switch a RE-RENDER rather than a
 * round trip: this app's own copy moved the instant the toggle was pressed and
 * the server-authored strings beside it did not move until a reissued token had
 * been sent and three reads asked again with it.
 *
 * Keys are `Locale` values (`LOCALES` in `packages/i18n`); the 10-character cap
 * is what a regional tag needs and nothing more. A language the catalogue has
 * no copy for is ABSENT from the map rather than filled with the default, so a
 * reader falls back knowingly (CAT-002).
 */
export const ByLocaleText = z.record(z.string().max(10), z.string().max(120));
export type ByLocaleText = z.infer<typeof ByLocaleText>;

/** The same, for a field a curator writes paragraphs into. */
export const ByLocaleLongText = z.record(z.string().max(10), z.string().max(1000));
export type ByLocaleLongText = z.infer<typeof ByLocaleLongText>;

/** The same, for a field that is a LIST: a recipe (CAT-002). */
export const ByLocaleList = z.record(z.string().max(10), z.array(z.string().max(120)).max(60));
export type ByLocaleList = z.infer<typeof ByLocaleList>;

/**
 * A catalogue line as the guest sees it. Sale prices are owned by the platform;
 * bakeries never set guest prices (PRD CAT-003).
 */
/**
 * One product that satisfies a bullet on a formule's menu card (CAT-018).
 *
 * Carries what the guest needs to CHOOSE between them and nothing else: the
 * name in their own language, and the allergens, because the choice changes
 * them - a tuna sandwich and a quiche do not declare the same thing, and a
 * picker that hid that would be asking a guest to pick blind on the one screen
 * the platform states allergens on (GST-003).
 */
/**
 * The two kinds of section a set's menu card has (CAT-018, founder decision
 * 2026-08-30). `INCLUDED` lists items that are all in the set, each with its
 * count - the guest is asked nothing. `CHOICE` lists options and the guest
 * picks ONE. No section is special at the counter (founder, 2026-09-25: keep
 * the switch, drop the main): a part short goes alone, unless the set is marked
 * inseparable (`bundled`), when the whole set goes (BAK-019).
 */
export const FormuleSectionKind = z.enum(['INCLUDED', 'CHOICE']);
export type FormuleSectionKind = z.infer<typeof FormuleSectionKind>;

export const FormuleChoice = z.object({
  itemId: PublicId,
  name: z.string().min(1).max(120),
  /**
   * How many of this item the set holds: the item's own count in an
   * `INCLUDED` section, and the section's count for an option of a `CHOICE`
   * section (the guest picks the kind, the section says how many).
   */
  quantity: z.int().positive(),
  /**
   * What picking THIS option adds to the set's price, per unit of the pick,
   * VAT included, in minor units (CAT-018, founder decision 2026-09-01). 0 for
   * every item of an `INCLUDED` section and for the options at a section's
   * floor; the sheet prints it beside the option and adds it to the button.
   * Always stated, because a client that has to guess whether a missing
   * figure is zero is a client that prices a line wrong.
   */
  supplement: MinorUnitsNonNegative,
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback.
   */
  nameByLocale: ByLocaleText.optional(),
  allergens: z.array(AllergenCode),
});
export type FormuleChoice = z.infer<typeof FormuleChoice>;

/**
 * One BULLET of a formule's menu card: what it is, how many, and what may
 * satisfy it (CAT-018).
 *
 * A bullet with one choice is a fixed ingredient and is shown as contents; a
 * bullet with several is a question, and the guest answers it before the
 * formule can go in the basket.
 */
export const FormuleBullet = z.object({
  /** The bullet's position on the card, and the key a basket choice names. */
  rank: z.int().min(0),
  /** What kind of section this is; a basket answer names a `CHOICE` one only. */
  kind: FormuleSectionKind,
  /** For a `CHOICE` section, how many of the picked option the set holds. */
  quantity: z.int().positive(),
  /** The bullet's own wording where the products alone do not say it; may be ''. */
  note: z.string().max(200),
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback. The long-text
   * variant, because a bullet's note runs to 200 characters and the short one
   * stops at 120 - a cap that refused a legal note would be a 500 under
   * response validation, on the one screen a formule is answered.
   */
  noteByLocale: ByLocaleLongText.optional(),
  choices: z.array(FormuleChoice).min(1).max(20),
});
export type FormuleBullet = z.infer<typeof FormuleBullet>;

export const CatalogueItem = z.object({
  id: PublicId,
  kind: ProductType,
  name: z.string().min(1).max(120),
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback.
   */
  nameByLocale: ByLocaleText.optional(),
  description: z.string().max(1000),
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback.
   */
  descriptionByLocale: ByLocaleLongText.optional(),
  /** Guest-facing sale price, VAT included, in integer minor units. */
  price: MinorUnitsNonNegative,
  currency: CurrencyCode,
  images: z.array(ProductImage).max(6),
  ingredients: z.array(z.string().max(120)).max(60),
  /**
   * Every shipped language (NFR-006); the reader picks with `listIn`, the
   * served field is the session's language and the fallback.
   */
  ingredientsByLocale: ByLocaleList.optional(),
  allergens: z.array(AllergenCode),
  dietaryTags: z.array(DietaryTag),
  availability: ItemAvailability,
  /** Merchandising rank; lower sorts first (PRD CAT-007). */
  rank: z.int().default(0),
  maxQuantityPerOrder: z.int().positive().nullable(),
  /**
   * A formule's menu card, so the guest can answer it before adding (CAT-018).
   *
   * ABSENT on a plain product, and absent on a formule whose card the storefront
   * could not resolve - a client that finds nothing here adds the item as it
   * always did, which is what every client older than this field does. Present
   * means the item has bullets, and a bullet offering several choices must be
   * answered: `checkout` refuses a basket line that leaves one open.
   */
  formule: z.array(FormuleBullet).max(20).optional(),
});
export type CatalogueItem = z.infer<typeof CatalogueItem>;

/**
 * PRD BAK-027 - the span in which a bag can be handed to a courier at a shop,
 * as local wall-clock times in the shop's own zone. This is the ONE thing a
 * bakery configures about its day (see `PickupWindowRule` in the bakery
 * contract): there is no separate opening or closing hour anywhere in this
 * product, and inventing one on a guest surface would put a second answer next
 * to the counter's own.
 *
 * `end` is null on a weekday left unbounded at the top, which is how every
 * location behaved before `latest_pickup` existed.
 */
export const PickupHours = z.object({
  start: LocalTime,
  end: LocalTime.nullable(),
});
export type PickupHours = z.infer<typeof PickupHours>;

/** A shop card in mode B, before the guest picks a shop (PRD GST-030, GST-031). */
export const ShopCard = z.object({
  id: PublicId,
  displayName: z.string().min(1).max(120),
  shortDescription: z.string().max(400),
  logoUrl: ImageUrl.nullable(),
  heroImageUrl: ImageUrl.nullable(),
  /** PRD CAT-014 - stated on the card before selection. */
  minimumBasket: MinorUnitsNonNegative.nullable(),
  currency: CurrencyCode,
  /**
   * BAK-027 - the HOUSE'S working hours for the morning this card is offered
   * for: a card is a brand, a brand is many counters, and this is the span
   * from the earliest any of them starts to the latest any of them hands a bag
   * to a courier (`pickupHoursSpan`, finding F-33). Null when every counter is
   * shut that weekday.
   */
  pickupHours: PickupHours.nullable(),
  /** The zone `pickupHours` is stated in - a shop's own, never the reader's. */
  pickupTimeZone: TimeZone,
  /**
   * The house's WEEK, one entry per weekday in `Date#getDay` order (0 = Sunday),
   * null on a day it is shut - `weeklyPickupHours` over every counter of the
   * house that can serve this address, so it is the same envelope as
   * `pickupHours`, asked seven times. The tile folds it with
   * `weeklyHoursGroups`; the words are the reader's catalogue's.
   */
  weeklyHours: z.array(PickupHours.nullable()).length(7),
  /**
   * Straight-line metres from the apartment to the NEAREST counter of the
   * house that can serve it - the figure allocation already measured
   * (`distanceMeters`, ALC-003), never a second computation. Which counter
   * serves is still allocation's answer once a window exists; this is how far
   * the house is, not a promise of who bakes. Null when no counter's
   * coordinates were usable.
   */
  distanceMeters: z.int().nonnegative().nullable(),
  /**
   * The first window of the union the picker behind this card offers, ON
   * `nextAvailableServiceDay` - one derivation, so card and picker cannot
   * disagree (GST-030, F-33). Null when the house offers no window at all on
   * that morning.
   *
   * The TILE does not render it: what a guest compares two houses on is when
   * each is open (`pickupHours`), not a delivery window, which is a promise
   * the picker makes once there is a basket and a morning to make it about.
   * It stays on the wire as the anchor that lets a test prove the card and the
   * picker were derived from the same union - the regression F-33 was.
   */
  earliestWindow: DeliveryWindow.nullable(),
  /**
   * The first morning this house can serve this address - the morning the two
   * windows above describe, and the one the card is offering.
   *
   * It is populated for EVERY card, not only for a house that is shut today.
   * Mode B chooses the house BEFORE the morning (GST-030), so at the moment
   * this card is read no morning has been chosen and "available today" is not a
   * question anyone has asked yet; what the guest wants to know is the first
   * date they could order for. Null only when the house offers no morning
   * inside the search horizon, which is also when it is not carded at all.
   */
  nextAvailableServiceDay: ServiceDay.nullable(),
  /**
   * Where that morning sits against the counters' own today, decided by the
   * platform's clock - the tile says "today" or "tomorrow" from this and never
   * asks the device what day it is (TST-003).
   */
  nextAvailableDayKind: z.enum(['TODAY', 'TOMORROW', 'LATER']),
  /**
   * The house's OPENING HOURS on that morning, as the house configured them:
   * its counters' services merged where they overlap (`pickupServicesOn`), one
   * for a house open straight through, two for a morning and an afternoon. The
   * `end` is the closing time the baker typed - never a delivery window's end,
   * which runs past it. On today, services already over are left out. Empty
   * when the house is shut that day.
   */
  deliverySpans: z.array(PickupHours).max(12),
  /**
   * RTE-034: today's ASAP row is on offer for this house - it can be ordered
   * from for NOW, which only holds while a counter is open. The tile then
   * states when delivery ENDS rather than when it starts.
   */
  orderableNow: z.boolean(),
  /**
   * Why the house is CLOSED on the morning the list was asked for - a weekly
   * closure, a recorded closure, a pause (allocation's `ClosureKind`, stated
   * here because contracts does not depend on allocation). The tile then says
   * the bakery is closed and when it reopens (`nextAvailableServiceDay`). Null
   * when the house is open that morning, even with nothing left to deliver.
   */
  closure: z
    .enum(['WEEKLY_CLOSURE', 'VACATION', 'EXCEPTIONAL_CLOSURE', 'NOT_OPEN', 'ORDERS_PAUSED'])
    .nullable(),
});
export type ShopCard = z.infer<typeof ShopCard>;

/**
 * WHAT A MODE B2 GUEST IS TOLD ABOUT THEIR SHOP - the dates, the times, the
 * reach and the minimum, and NOTHING that identifies it (founder decision,
 * 2026-09-20).
 *
 * The same facts a `ShopCard` carries, minus every identity field: no `id`, no
 * `displayName`, no `logoUrl`, no `heroImageUrl`, no `shortDescription`. In
 * mode B2 the platform picks the closest mode B shop at every scan and the
 * guest is never shown which bakery it is - not on the entry screen, not on the
 * menu, not on the order, not on the receipt.
 *
 * It is a SEPARATE schema rather than `ShopCard.omit(...)`, and `.strict()`,
 * for the reason the split exists at all: a field added to the card must not
 * arrive here by inheritance. A new identity field on `ShopCard` leaves this
 * shape untouched; a new one added HERE has to be argued for in this comment
 * first.
 *
 * `earliestWindow` is deliberately absent too. It is the card's anchor for
 * proving the tile and the picker share one union (F-33); the B2 entry screen
 * offers "order now" or "schedule", and both go to the picker, which makes its
 * own offer.
 */
export const ShopFacts = z
  .object({
    /** PRD CAT-014 - the house's minimum still applies, as an amount alone. */
    minimumBasket: MinorUnitsNonNegative.nullable(),
    currency: CurrencyCode,
    /**
     * BAK-027 - the bound shop's working hours on `nextAvailableServiceDay`,
     * or on today when it can serve no morning inside the horizon. Null when
     * it is shut that weekday.
     */
    pickupHours: PickupHours.nullable(),
    /** The zone `pickupHours` and `deliverySpans` are stated in - the shop's own. */
    pickupTimeZone: TimeZone,
    /**
     * The shop's WEEK, one entry per weekday in `Date#getDay` order
     * (0 = Sunday), null on a day it is shut. This is what the "schedule your
     * delivery" button states: which days this bakery opens, and when.
     */
    weeklyHours: z.array(PickupHours.nullable()).length(7),
    /**
     * Straight-line metres from the apartment - the ONE criterion that chose
     * this shop (`closestShopFor`). Never null here: a shop with unusable
     * coordinates could not have been chosen.
     */
    distanceMeters: z.int().nonnegative(),
    /**
     * The first morning this shop can serve this address, inside the search
     * horizon. Null when there is none - the screen then says it is shut and
     * the hours above are today's.
     */
    nextAvailableServiceDay: ServiceDay.nullable(),
    /**
     * Where the morning the hours describe sits against the shop's own today,
     * decided by the platform's clock - the screen never asks the device what
     * day it is (TST-003).
     */
    nextAvailableDayKind: z.enum(['TODAY', 'TOMORROW', 'LATER']),
    /**
     * The shop's OPENING HOURS on that morning, as configured
     * (`pickupServicesOn`): one span for a shop open straight through, two for
     * a morning and an afternoon. The `end` is the closing time the baker
     * typed, never a delivery window's end. Empty when the shop is shut.
     */
    deliverySpans: z.array(PickupHours).max(12),
    /**
     * RTE-034: today's ASAP row is on offer, which only holds while the shop
     * is open. This is what enables the "order now" button.
     */
    orderableNow: z.boolean(),
    /**
     * "Delivery in about N min": one minute to accept, this shop's own measured
     * preparation time (the platform's default until it has traded enough to
     * have one), and the delivery provider's travel from this shop to this
     * apartment - whole minutes, never rounded to five (founder, 2026-09-21).
     * Null exactly when `orderableNow` is false. Argued for here, as this
     * schema requires: it is a duration, and it names nobody.
     *
     * An ESTIMATE the guest reads before there is a basket. The arrival RANGE
     * is still the quote's, on the review (RTE-034).
     */
    orderNowMinutes: z.int().positive().nullable(),
  })
  .strict();
export type ShopFacts = z.infer<typeof ShopFacts>;

// ---------------------------------------------------------------------------
// Property and delivery brief
// ---------------------------------------------------------------------------

export const AddressInstruction = z.object({
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).nullable(),
  postcode: z.string().min(1).max(20),
  city: z.string().min(1).max(100),
  countryCode: z.string().length(2).default('FR'),
  building: z.string().max(120).nullable(),
  floor: z.string().max(40).nullable(),
  doorPosition: DoorPosition.nullable(),
  doorNumber: DoorNumber,
  intercomName: z.string().max(120).nullable(),
  /**
   * The building code, in plain text, to everybody entitled to the brief
   * (GST-024, founder decision 2026-08-24).
   *
   * It used to be masked from the guest and handed over only by an explicit
   * tap on a logged endpoint. That protected the wrong thing: the guest is
   * standing in the building and has to key this code in to get through the
   * door, and the courier is given it in the delivery note - it is not a secret
   * from either of them, and a box the guest had to tap to read was friction
   * over a number already on their key fob. NFR-009 is untouched and still
   * governs where it may NOT appear: broad dashboards, exports, and the
   * provider once the delivery is done.
   */
  accessCode: z.string().max(60).nullable(),
  lift: z.boolean().nullable(),
  accessNotes: z.string().max(1000).nullable(),
  /**
   * GST-007: the apartment's leave-at-door default (founder decision
   * 2026-08-23). `null` is "not stated", which reads as no - the courier hands
   * the bag over unless this apartment, or the guest's own switch at checkout,
   * says otherwise.
   */
  leaveAtDoor: z.boolean().nullable(),
  timeZone: TimeZone,
  coordinates: GeoPoint.nullable(),
});
export type AddressInstruction = z.infer<typeof AddressInstruction>;

/**
 * Guest edits at checkout are an order-scoped override snapshotted onto the order
 * (PRD GST-023). They never mutate the conciergerie's property record.
 */
export const DeliveryOverride = z.object({
  /*
   * The brief the apartment carries, field for field (founder decision
   * 2026-08-24). The guest's sheet and the owner's Delivery brief are the same
   * eight boxes in the same order, so every box on one has somewhere to go on
   * the other - `building` and `lift` were the two that did not, and a guest
   * correcting "Building B" or "the lift is out" had nowhere to say it.
   */
  building: z.string().max(120).nullable(),
  floor: z.string().max(40).nullable(),
  doorPosition: DoorPosition.nullable(),
  doorNumber: DoorNumber,
  intercomName: z.string().max(120).nullable(),
  accessCode: z.string().max(60).nullable(),
  lift: z.boolean().nullable(),
  /**
   * The name the courier asks for.
   *
   * No longer COLLECTED (founder decision 2026-08-24): the delivery sheet asks
   * the eight questions the apartment's brief asks and no ninth, and the name
   * the courier is given is the one on the payment - a wallet or a card knows
   * it, so nobody is asked twice (GST-006). The field stays on the wire because
   * orders placed before that decision carry one, and the order page still
   * reads it back; a new order sends `null`.
   */
  recipientName: z.string().max(120).nullable(),
  /** Free text for the courier. Never printed on the bag label (PRD GST-025). */
  courierInstructions: z.string().max(1000).nullable(),
  /**
   * GST-007: leave the bag at the door instead of handing it over. `null` is
   * "the guest did not say", so the apartment's own default decides; `false` is
   * a guest who turned an apartment default OFF for this order.
   */
  leaveAtDoor: z.boolean().nullable(),
});
export type DeliveryOverride = z.infer<typeof DeliveryOverride>;

export const PropertySummary = z.object({
  id: Uuid,
  reference: z.string().min(1).max(80),
  addressLine1: z.string(),
  /** The second street line, or null; the partner tables print it (D8, 2026-10-05). */
  addressLine2: z.string().nullable(),
  postcode: z.string(),
  city: z.string(),
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
  /** PRD GST-040 / CON-001 - the partner sees the same servability the guest does. */
  servable: z.boolean(),
  storefrontMode: StorefrontMode,
  /**
   * Does the apartment hold its QR code yet? One code per apartment, for life
   * (CON-003): it is minted with the apartment while the WEB-014 allowance has
   * room, or by the first sheet after a mass import, and serves from the moment
   * it exists. There is no other state a code can be in, so this is one bit -
   * `projections/apartment-state.ts` folds it into the row's one word.
   */
  hasQrCode: z.boolean(),
});
export type PropertySummary = z.infer<typeof PropertySummary>;

// ---------------------------------------------------------------------------
// Money: the price breakdown and the allocation snapshot
// ---------------------------------------------------------------------------

/**
 * PRD GST-022 - every component on its own line, in euros, in the guest's language.
 *
 * **Every charge row here is VAT-inclusive**, and `taxTotal` is the VAT already
 * contained in those rows rather than a further charge. The identity a screen
 * may rely on is therefore
 *
 *   merchandiseSubtotal + deliveryFee + serviceFee + smallOrderFee
 *     - promotionDiscount = total
 *
 * with `taxTotal` sitting outside it as "of which VAT".
 *
 * That is not a stylistic choice. The guest buys from a menu priced in
 * consumer prices, which in France are VAT-inclusive: the catalogue card, the
 * basket and every order line are gross. A breakdown whose rows were net could
 * not be reconciled with the lines printed directly above it - two croissants
 * and a juice added to 10.56 while the "Items" row said 10.00 - and adding the
 * lines to net fee rows and a full VAT row overshot the total by the
 * merchandise VAT. Stating the rows the way the lines are stated is what makes
 * the itemisation add up on screen, which is what GST-022 is for.
 *
 * The net amounts have not gone anywhere: `merchandiseNet` carries the
 * commission base (PAY-004), and the per-line VAT treatment PAY-022 requires is
 * on the receipt, which has each line's own rate.
 */
export const PriceBreakdown = z.object({
  currency: CurrencyCode,
  /**
   * The merchandise row of the itemised total (PRD GST-022), VAT included - the
   * sum of the order lines the guest is looking at, to the cent.
   */
  merchandiseSubtotal: MinorUnitsNonNegative,
  /** Merchandise excluding VAT - the base of every percentage (PRD PAY-004). */
  merchandiseNet: MinorUnitsNonNegative,
  /** Delivery charge, VAT included at the standard service rate (PAY-019, PAY-022). */
  deliveryFee: MinorUnitsNonNegative,
  /** Service charge, VAT included at the standard service rate (PAY-018, PAY-022). */
  serviceFee: MinorUnitsNonNegative,
  /** Small-order fee where one is configured, VAT included (PAY-023). */
  smallOrderFee: MinorUnitsNonNegative,
  promotionDiscount: MinorUnitsNonNegative,
  /**
   * The VAT *contained in* the rows above, never added to them. Rendered as
   * "of which VAT", so a guest who adds the rows still lands on `total`.
   */
  taxLines: z.array(
    z.object({
      label: z.string().max(80),
      rateBasisPoints: BasisPoints,
      amount: MinorUnitsNonNegative,
    }),
  ),
  taxTotal: MinorUnitsNonNegative,
  /** What is actually charged. */
  total: MinorUnitsNonNegative,
});
export type PriceBreakdown = z.infer<typeof PriceBreakdown>;

/**
 * The split snapshotted onto the order at placement (PRD PAY-003, PAY-021). A
 * later rate change never rewrites history.
 *
 * Two figures are read as the order STANDS rather than as it was placed, and
 * both come off the one money view: `bakeryPayable` (what the house is owed
 * once the counter's removals and every refund decided against it are taken
 * off - `bakeryPayableDue`) and `ownerCommission` (net of refunds). Until
 * either moves they equal the placed split.
 */
export const AllocationSnapshot = z.object({
  ruleVersion: z.string().max(40),
  effectiveAt: Instant,
  currency: CurrencyCode,
  ownerCommissionBasisPoints: BasisPoints,
  deliveryPassThroughBasisPoints: BasisPoints,
  /**
   * The fixed delivery price the order was placed under, VAT included; null
   * when the delivery was priced as a share of the courier's cost, in which
   * case the rate above is what priced it (PAY-019). Optional on the wire only
   * so a screen built before the field still parses; the handler always states it.
   */
  deliveryFixedFee: MinorUnitsNonNegative.nullable().optional(),
  /**
   * True when `deliveryFixedFee` is a stay's averaged delivery fee - the price
   * every morning of the stay was placed at, whatever its own courier costs
   * (#2156 decision 3) - rather than a fixed price of the delivery grid.
   * Optional on the wire only so a screen built before the field still parses;
   * the handler always states it.
   */
  deliveryStayAverage: z.boolean().optional(),
  /**
   * What the baker is owed now: the placed payable less the units the counter
   * removed and the refunds decided against the house (BAK-019, PAY-030) - the
   * figure the baker's own Orders row, the payout and Stripe's transfer carry.
   *
   * SIGNED: below zero when a decided case charged the house more than this
   * order owes it - the recorded decision of 29 September (#1719,
   * docs/finance/decisions.html#d0929-cancel-fee-cycle), recovered from the
   * house's other transfers in its cycle. It was non-negative here, which
   * that decision never reached: WAS4Q6 (sim 2026-10-02) charged a house owed
   * 25.56 for 59.98, and this order's detail answered 500 to the operator 176
   * times. Whether a charge may exceed the order is founder question #23.
   */
  bakeryPayable: MinorUnits,
  /** The conciergerie's or independent owner's commission, BEFORE VAT, net of refunds. */
  ownerCommission: MinorUnitsNonNegative,
  /**
   * The VAT it adds on top when VAT-registered (the flag the order's transfer
   * froze, else its status now), zero otherwise - its money, never the
   * platform's - and the two together: what its transfer carries, and the
   * figure the finance ledger shows for the commission.
   */
  ownerCommissionVat: MinorUnitsNonNegative,
  ownerCommissionIncludingVat: MinorUnitsNonNegative,
  platformMargin: MinorUnits,
  serviceFeeRevenue: MinorUnitsNonNegative,
  deliveryFeeRevenue: MinorUnitsNonNegative,
  /**
   * What the PROVIDER quoted, which is what the guest's delivery fee was priced
   * FROM (PRD PAY-020) - never what the guest paid for delivery. The two differ
   * by the VAT on the fee (and by the pass-through rate whenever it is not
   * 100%), and stating this figure as the delivery line of an order's
   * financials is how an internal screen came to print 15.00 for a delivery the
   * guest was charged 18.00 for. What the guest paid is `PriceBreakdown`'s own
   * `deliveryFee`, VAT included, beside every other row of the receipt.
   */
  expectedDeliveryCost: MinorUnitsNonNegative,
  /** Known only once the provider bills it; both are stored (PRD RTE-027). */
  actualDeliveryCost: MinorUnitsNonNegative.nullable(),
  /**
   * The VAT the PLATFORM remits out of the guest total: the standard rate on
   * its own two supplies, its commission to the house and its fees to the
   * guest. NOT the VAT the guest paid - that one also contains the food VAT,
   * which is the seller's and is settled inside the house price it was paid
   * with. The two are a few euros apart on every order and this bucket is the
   * one the settlement rows add up with (PAY-008). As the order stands: the
   * commission VAT now plus the fee rows' VAT (`vatRemittanceDue`).
   */
  taxTotal: MinorUnitsNonNegative,

  /*
   * The receipt read three ways per row - HT, VAT, TTC - so the financials card
   * prints all three rather than recovering one by subtracting two others
   * (OPS-002). Every figure comes from the one money view.
   */
  /**
   * The VAT on the platform's own commission to the house, at the standard
   * rate, as the order stands - like `platformMargin`, it moves with every
   * removal and refund (founder, 15 October 2026). With `bakeryPayable`,
   * `ownerCommission` and `platformMargin` it partitions the merchandise the
   * guest paid for, exactly, until something moves - which is what lets the
   * financials card state that partition rather than close it by subtracting
   * three figures from a fourth. After that, `platformRefundCost` closes it.
   */
  commissionVat: MinorUnitsNonNegative,
  /**
   * The part of `commissionVat` the platform keeps: less the VAT a
   * VAT-registered owner adds on its commission, which the platform
   * pays inside the owner's transfer and deducts as input VAT. With
   * `bakeryPayable`, `ownerCommissionIncludingVat` and `platformMargin` it is
   * the partition of the merchandise the financials card prints.
   */
  platformCommissionVat: MinorUnits,
  /**
   * What the refunds and the counter's removals cost the PLATFORM beyond the
   * commission it no longer earns: what went back to the guest less what came
   * off the baker's figure and off the platform's own commission. The platform
   * rows less this, with the two party figures and the order's
   * `refundedTotal`, add up to what the guest paid - before and after any
   * refund (OPS-006).
   */
  platformRefundCost: MinorUnits,
  /**
   * The platform's share of what the guest paid as the order stands: the
   * guest total less `refundedTotal`, `bakeryPayable` and
   * `ownerCommissionIncludingVat`, stated by the money view - the owner's VAT
   * is never the platform's. The finance ledger's platform revenue for a
   * delivered order is this less the courier's cost.
   */
  platformShare: MinorUnits,
  /** VAT inside the merchandise row, at the LINES' own food rates. */
  merchandiseVat: MinorUnitsNonNegative,
  /** VAT inside the delivery fee row the guest paid. */
  deliveryFeeVat: MinorUnitsNonNegative,
  /** VAT inside the service fee row the guest paid. */
  serviceFeeVat: MinorUnitsNonNegative,
  /** The guest total without the VAT it contains - the order's taxable base. */
  totalExcludingVat: MinorUnitsNonNegative,
});
export type AllocationSnapshot = z.infer<typeof AllocationSnapshot>;

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

/**
 * One order line AS BAGGED. After a stock-out replacement (BAK-019) the
 * line-level `itemId`, `name` and `allergens` are the REPLACEMENT's — the
 * three move together, exactly as they do on the counter's ticket, because a
 * line that swaps two of the three describes a product that does not exist,
 * and the allergen declaration is the one field where that is dangerous
 * (GST-020, CAT-004). The prices stay what the guest agreed to (CAT-006).
 * The placement identity survives on
 * `substitution.originalItemId` / `originalName`.
 */
/**
 * One component of a formule, as it was sold (plan section 7).
 *
 * A formule is ONE basket line at one price, with these listed under it: the
 * guest sees what the menu card promised, the counter packs them, and the payout
 * sums their bakery prices. Snapshotted at placement, so a product retired next
 * month still renders on the invoice that names it.
 *
 * `bakeryPrice` is what the HOUSE keeps, which is why this shape belongs to the
 * guest and internal surfaces and never to a bakery-facing one beside a guest
 * figure (BAK-029).
 */
export const OrderLineComponent = z.object({
  itemId: PublicId,
  /** `catalogue_items.public_id` at placement - what an invoice line names. */
  productCode: PublicId,
  name: z.string().max(120),
  /**
   * Units of this part the counter has recorded as not in the bag and the
   * platform refunded (BAK-019), out of `quantity` x the line's count. Absent
   * means none.
   */
  removedQuantity: z.int().nonnegative().optional(),
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback.
   */
  nameByLocale: ByLocaleText.optional(),
  quantity: z.int().positive(),
  /**
   * What the HOUSE keeps for this one, VAT-inclusive - and it is off this wire
   * (BAK-029 read the other way round).
   *
   * The guest's order detail is where this shape is actually served, and it
   * carried the house's cut per component to every guest who opened a formule:
   * guest price on the line, house price under it, so the platform's margin was
   * a subtraction anybody could do in their head. No screen rendered it, which
   * is exactly why it survived - a figure nothing displays is still in the
   * response body, the query cache and the network tab.
   *
   * Optional rather than deleted because the field is what the payout sums and
   * an invoice line names, and a reader that predates the removal must not
   * fail validation on its absence. Nothing on the wire fills it today: the
   * order's money is read from the ONE money view
   * (`packages/services/src/order-money.ts`), never off a component.
   */
  bakeryPrice: MinorUnitsNonNegative.optional(),
  /** The food rate this component carries - a formule spans two of them. */
  vatRateBps: BasisPoints,
  /** What it would have sold for on its own, at placement. */
  listSalePrice: MinorUnitsNonNegative,
  /**
   * What the GUEST paid on top of the set's price for picking this one, per
   * unit of it, at placement (CAT-018, founder decision 2026-09-01). The line's
   * `unitPrice` already includes it - this is the record of WHY that price is
   * what it is, and what a refund of this part hands back over its share.
   * Absent on a line placed before supplements existed, read as 0.
   */
  supplement: MinorUnitsNonNegative.optional(),
});
export type OrderLineComponent = z.infer<typeof OrderLineComponent>;

export const OrderLine = z.object({
  id: Uuid,
  itemId: PublicId,
  /**
   * The product code an invoice line refers to (plan section 9): the catalogue
   * item's public id at placement. Optional because every line placed before it
   * existed has none, and a reader with no code names the product instead.
   */
  productCode: PublicId.optional(),
  name: z.string().max(120),
  /**
   * Every shipped language (NFR-006); the reader picks with `textIn`, the
   * served field is the session's language and the fallback.
   */
  nameByLocale: ByLocaleText.optional(),
  quantity: z.int().positive(),
  /** A formule's contents as sold; absent on a plain product (section 7). */
  components: z.array(OrderLineComponent).max(20).optional(),
  /**
   * CAT-018 as amended 2026-09-25: this formule is packed as one - the
   * counter takes a whole box off when it is short of any part. Written only
   * when true, exactly as stored; absent reads as false.
   */
  bundled: z.boolean().optional(),
  /** Snapshotted at placement (PRD CAT-006) — never the replacement's price. */
  unitPrice: MinorUnitsNonNegative,
  lineTotal: MinorUnitsNonNegative,
  allergens: z.array(AllergenCode),
  substitution: z
    .object({
      replacedWithItemId: PublicId.nullable(),
      replacedWithName: z.string().max(120).nullable(),
      /**
       * Every shipped language (NFR-006); the reader picks with `textIn`, the
       * served field is the session's language and the fallback.
       */
      replacedWithNameByLocale: ByLocaleText.optional(),
      /**
       * The replacement's allergen declaration as snapshotted at swap time;
       * null on the refund arm and on swaps recorded before it was snapshotted
       * (the line-level list then falls back to the placement declaration).
       */
      allergens: z.array(AllergenCode).nullable(),
      /** The line as agreed at placement (CAT-006): what the swap replaced. */
      originalItemId: PublicId,
      originalName: z.string().max(120),
      /**
       * Every shipped language (NFR-006); the reader picks with `textIn`, the
       * served field is the session's language and the fallback.
       */
      originalNameByLocale: ByLocaleText.optional(),
      /**
       * How many UNITS of the line were removed and refunded (BAK-019).
       *
       * A counter that had four of six sets the line to four, and two units come
       * back; a counter with none sets it to zero and the whole line does. Both
       * are one annotation, so the number is what tells the two apart - without
       * it a line short by one unit and a line refunded outright are
       * indistinguishable on this screen, and the screen struck them both
       * through. Zero on the replace arm, where nothing was removed: the
       * equivalent is being baked instead.
       */
      removedQuantity: z.int().nonnegative(),
      reason: z.string().max(200),
    })
    .nullable(),
});
export type OrderLine = z.infer<typeof OrderLine>;

export const CourierInfo = z.object({
  /** PRD GST-035 - a field the provider does not supply is omitted, never faked. */
  firstName: z.string().max(80).nullable(),
  photoUrl: ImageUrl.nullable(),
  vehicleType: z.string().max(60).nullable(),
  vehicleMake: z.string().max(60).nullable(),
  vehicleModel: z.string().max(60).nullable(),
  vehicleColour: z.string().max(40).nullable(),
  /** Masked by the provider (its last characters); shown as given. */
  licensePlate: z.string().max(20).nullable().optional(),
  location: GeoPoint.nullable(),
  locationUpdatedAt: Instant.nullable(),
  /**
   * THE ROUTE TAKEN WITH THE BAG (GST-035), oldest first - `location` is only
   * its last point, and a guest who reloads or locks their phone loses the run
   * they were watching without this.
   *
   * ALWAYS PRESENT, empty when there is nothing, and deliberately neither
   * optional nor defaulted. A `.default([])` here would be a parsing
   * convenience the wire never sees: the handler that omitted the key would
   * validate clean on the way out, a reader that does not parse (a script, the
   * Sunmi) would receive it `undefined`, and a screen spreading it into an
   * array would throw.
   *
   * Only the post-pickup leg is ever in it, and in mode A the points near the
   * counter are withheld from the guest (CAT-009) - both decided by the server,
   * so no screen has to know about storefront modes.
   */
  trail: z.array(GeoPoint.extend({ at: Instant })),
  /** Provider's anonymised number and PIN where exposed. */
  maskedPhone: z.string().max(40).nullable(),
  maskedPhonePin: z.string().max(20).nullable(),
  /** The provider's package id where it differs from the job's (Stuart's Package ID). */
  packageId: z.string().max(80).nullable(),
  trackingUrl: z.url().nullable(),
});
export type CourierInfo = z.infer<typeof CourierInfo>;

export const ProofOfDelivery = z.object({
  photoUrl: ImageUrl.nullable(),
  pin: z.string().max(20).nullable(),
  signatureUrl: ImageUrl.nullable(),
  deliveredAt: Instant,
});
export type ProofOfDelivery = z.infer<typeof ProofOfDelivery>;

/** The guest-visible timeline entry (PRD GST-036). Stages only advance. */
export const StageEntry = z.object({
  stage: GuestStage,
  reachedAt: Instant.nullable(),
  current: z.boolean(),
  /**
   * i18n catalogue key; the client renders it in the guest's locale (NFR-006).
   * Closed to the seven stage keys (`messages.ts`), so the string the worker
   * composes and the catalogue the guest app reads cannot drift apart silently.
   */
  messageKey: GuestStageMessageKeySchema,
  /** Parameters for the catalogue string, e.g. courier name, day, window. */
  messageParams: z.record(z.string(), z.string()),
});
export type StageEntry = z.infer<typeof StageEntry>;

export const OrderSummary = z.object({
  id: Uuid,
  publicId: PublicId,
  /** The code handwritten on the bag; pairs courier and bag at the counter (BAK-017). */
  shortCode: z.string().min(4).max(10),
  lifecycle: OrderLifecycleWire,
  summary: OrderSummaryState,
  stage: GuestStage,
  serviceDay: ServiceDay,
  /**
   * The window the guest reads (PRD GST-008): the provider's dropoff opening to
   * its arrival estimate as last reported, the order's own promise until a
   * booking has said either. Never the provider's deadline.
   */
  promisedWindow: DeliveryWindow,
  /** The window's end as a single instant, where a screen wants one (PRD RTE-009). */
  estimatedArrival: Instant.nullable(),
  storefrontMode: StorefrontMode,
  fulfillmentMode: FulfillmentMode,
  /** Named only in mode B; in mode A the bakery is surfaced nowhere (PRD CAT-009). */
  shopDisplayName: z.string().max(120).nullable(),
  /**
   * The AGREED total — the placement snapshot, deliberately NOT net of
   * refunds: a summary row names the order by what it was for, and the
   * movement half (refunded / netTotal) lives on `OrderDetail.money`, the
   * screen with room to state both. One derivation, two stated roles — not
   * two answers to one question.
   */
  total: MinorUnitsNonNegative,
  currency: CurrencyCode,
  itemCount: z.int().nonnegative(),
  /**
   * Units the counter took off for a stock-out (BAK-019): what the row's
   * "Sold out" badge counts, so the list says something is missing before the
   * guest opens the order. Zero on an order nobody changed; always present.
   */
  removedItemCount: z.int().nonnegative(),
  placedAt: Instant,
  /** PRD GST-009 - present exactly while the guest may still cancel. */
  cancellable: z.boolean(),
});
export type OrderSummary = z.infer<typeof OrderSummary>;

// ---------------------------------------------------------------------------
// Financial documents (docs/finance/invoicing.html)
// ---------------------------------------------------------------------------

/**
 * The five documents the platform produces (invoicing section 1): the guest's
 * fee invoice and the baker's product invoice issued under mandate, the
 * baker's commission invoice, the conciergerie's or corporate owner's
 * self-bill, and a private owner's statement. The same list, in the same order,
 * as the shared `DOCUMENT_FAMILIES` tuple.
 */
export const DocumentFamily = z.enum(DOCUMENT_FAMILIES);
export type DocumentFamily = z.infer<typeof DocumentFamily>;

/** An invoice, the credit note that cancels one in full, or a statement. */
export const DocumentNature = z.enum(DOCUMENT_NATURES);
export type DocumentNature = z.infer<typeof DocumentNature>;

/**
 * DRAFT while a payout preview holds it, ISSUED once numbered, CANCELLED once a
 * credit note has cancelled it. No reader outside Internal is ever shown a
 * DRAFT; the state is on the wire so a partner's list can say which of its
 * invoices a credit note cancelled.
 */
export const DocumentState = z.enum(DOCUMENT_STATES);
export type DocumentState = z.infer<typeof DocumentState>;

/**
 * Where a document stands with Pennylane (invoicing section 9): never sent
 * (a product invoice, a statement), waiting, sent, or refused.
 */
export const AccountingState = z.enum(ACCOUNTING_STATES);
export type AccountingState = z.infer<typeof AccountingState>;

/**
 * One document as a list names it (invoicing sections 8 and 10): what it is,
 * its number and date, the three totals it prints, the document it cancels or
 * replaces and the one that cancelled it, and where its stored PDF is served.
 *
 * `downloadUrl` is absolute and is the ROUTE that serves the stored bytes -
 * behind the reader's own gate (a partner's session, a guest's signature) -
 * never a bucket address: an invoice is served from the documents bucket and
 * never from /media. The totals are the sums of the lines the PDF prints,
 * negative on a credit note.
 */
export const DocumentSummary = z.object({
  id: Uuid,
  family: DocumentFamily,
  nature: DocumentNature,
  state: DocumentState,
  /** Null only while DRAFT: a number is assigned at issue, never before. */
  number: z.string().min(1).max(40).nullable(),
  /** The invoice date; null only while DRAFT. */
  issuedOn: ServiceDay.nullable(),
  totals: z.object({
    excludingTaxMinor: MinorUnits,
    taxMinor: MinorUnits,
    includingTaxMinor: MinorUnits,
  }),
  referencesDocumentId: Uuid.nullable(),
  cancelledByDocumentId: Uuid.nullable(),
  downloadUrl: RemoteObjectUrl,
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

/**
 * Where the guest's product invoice stands once it exists or they asked
 * (invoicing.html#delivery): ISSUED - or owed nothing, when no merchandise was
 * kept - or PENDING
 * while the house's legal block is incomplete and the nightly retry waits on it.
 */
export const ProductsInvoiceStanding = z.enum(['ISSUED', 'PENDING']);
export type ProductsInvoiceStanding = z.infer<typeof ProductsInvoiceStanding>;

export const OrderDetail = OrderSummary.extend({
  locale: Locale,
  lines: z.array(OrderLine),
  price: PriceBreakdown,
  /** Whether the guest's card was actually charged (GST-022). */
  money: z.object({ charged: z.boolean() }),
  timeline: z.array(StageEntry),
  courier: CourierInfo.nullable(),
  /**
   * Where the bag left from - the start of the courier's run on the GST-035
   * map, and the one thing about the shop this wire states in BOTH modes
   * (founder decision, 2026-09-08). It is drawn as an unnamed dot, and a
   * coordinate with no name, no brand and no address beside it is not the
   * identity CAT-009 withholds - `shopDisplayName` still is, and is null in
   * mode A. Both come from one read in the handler, so they cannot disagree
   * about which mode this is.
   *
   * Nullable because a counter that fails to resolve has no point to send,
   * never because of the mode.
   */
  shopCoordinates: GeoPoint.nullable(),
  proofOfDelivery: ProofOfDelivery.nullable(),
  delivery: z.object({
    address: AddressInstruction,
    /**
     * The apartment's own coordinates - the fixed end of the courier's run on
     * the GST-035 map. The guest is standing in it; there is nothing here they
     * do not already know, and without it the map can frame a moving pin
     * against nothing.
     */
    coordinates: GeoPoint,
    override: DeliveryOverride.nullable(),
    recipientFirstName: z.string().max(80),
    recipientPhone: PhoneE164,
  }),
  substitutionChoice: SubstitutionChoice,
  acquisitionChannel: AcquisitionChannel,
  /**
   * PRD PAY-022 - the signed link to this order's receipt PDF.
   *
   * Not nullable: it used to be absent until the order was captured, which left
   * the order screen with a null to design around and it chose an in-app
   * summary page - the intermediate screen the download button exists to skip.
   * The document is renderable from the moment the order is, and says on its
   * face whether the money has moved yet.
   */
  receiptUrl: z.url(),
  /**
   * The guest's invoices (docs/finance/invoicing.html#delivery). The fee
   * invoice is issued at the closing and WAITS: nothing is listed until
   * invoice 1 - the food, in the Baker's name, issued only on request - exists
   * or the guest asks, and then every document of the order is - the two
   * invoices, and any credit note and corrected invoice a later refund issued.
   * `invoicesAvailable` is whether a request is accepted (the order has closed
   * with something to invoice); `productsInvoice` is null while invoice 1 does
   * not exist and nobody asked ("Request an invoice"), PENDING while the
   * house's legal block keeps it back, ISSUED once it exists ("Invoice").
   */
  invoicesAvailable: z.boolean(),
  invoiceRequestedAt: Instant.nullable(),
  productsInvoice: ProductsInvoiceStanding.nullable(),
  documents: z.array(DocumentSummary),
  tip: z
    .object({
      amount: MinorUnitsNonNegative,
      chargedAt: Instant,
    })
    .nullable(),
  /**
   * PAY-024 / GST-037: may this order's guest tip its courier at all.
   *
   * A CAPABILITY of the adapter holding the job, which is not the same question
   * as "has the bag arrived" - and the screen only ever knew the second. It drew
   * the tip control on any delivered order, so an order riding with a provider
   * that forwards no tips took the guest through choosing an amount and answered
   * 409 at the end of it (`TipNotSupportedError`). With two adapters configured
   * that is not a hypothesis: one declares `COURIER_TIP` and one does not, and
   * which one an order has is a fact about the ORDER.
   *
   * False for an order with no booking, which cannot be tipped either.
   */
  tipOffered: z.boolean(),
  ratings: z.array(
    z.object({
      subject: RatingSubject,
      stars: z.int().min(1).max(5),
      comment: z.string().max(2000).nullable(),
    }),
  ),
});
export type OrderDetail = z.infer<typeof OrderDetail>;

// ---------------------------------------------------------------------------
// Delivery jobs and providers
// ---------------------------------------------------------------------------

export const ProviderRefund = z.object({
  refundRef: z.string().max(120),
  occurredAt: Instant,
  currency: z.string().length(3),
  providerRefund: MinorUnitsNonNegative,
  platformRefund: MinorUnitsNonNegative,
  /**
   * THE ORDER's running totals, across every credit the provider raised against
   * it - not this one's. The two above are the LATEST credit's own, which is
   * what the panel prints beside its reasons, and an order credited twice read
   * the second credit alone as though it were the order's whole story.
   *
   * Off `orderMoneyView` (`providerCredited` / `providerHeldLiable`), which is
   * where every figure about one order's money is derived exactly once
   * (check-money-derivation).
   */
  totalProviderRefund: MinorUnitsNonNegative,
  totalPlatformRefund: MinorUnitsNonNegative,
  lines: z.array(
    z.object({
      reason: z.string().max(80),
      partyAtFault: z.enum(['PROVIDER', 'PLATFORM', 'UNKNOWN']),
      providerRefund: MinorUnitsNonNegative,
      platformRefund: MinorUnitsNonNegative,
    }),
  ),
});
export type ProviderRefund = z.infer<typeof ProviderRefund>;

export const DeliveryJobSummary = z.object({
  id: Uuid,
  orderId: Uuid,
  status: DeliveryJobStatus,
  /** Opaque provider reference; no provider-specific column on a shared table (RTE-023). */
  providerReference: z.string().max(200).nullable(),
  providerName: z.string().max(60),
  quotedFee: MinorUnitsNonNegative.nullable(),
  chargedFee: MinorUnitsNonNegative.nullable(),
  collectReadyAt: Instant.nullable(),
  collectDeadlineAt: Instant.nullable(),
  deliverReadyAt: Instant.nullable(),
  deliverDeadlineAt: Instant.nullable(),
  estimatedArrival: Instant.nullable(),
  /** When the courier is expected at the pickup, where the provider says. */
  estimatedPickup: Instant.nullable().optional(),
  actualArrival: Instant.nullable(),
  courier: CourierInfo.nullable(),
  /**
   * A refund the provider raised against this delivery (a damaged bag, a late
   * courier), in minor units: what it credits the platform and what it holds
   * the platform liable for. Null until one arrives.
   */
  providerRefund: ProviderRefund.nullable().optional(),
});
export type DeliveryJobSummary = z.infer<typeof DeliveryJobSummary>;

export const ProviderCapabilityDeclaration = z.object({
  providerName: z.string().max(60),
  capabilities: z.array(ProviderCapability),
  /** Surfaced in the operations portal so a missing capability is visible (RTE-022). */
  fallbacks: z.record(z.string(), z.string()),
});
export type ProviderCapabilityDeclaration = z.infer<typeof ProviderCapabilityDeclaration>;

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export const OrganizationSummary = z.object({
  id: Uuid,
  /** The business name (founder, 2026-09-29), shown with `externalId` beside it. */
  name: z.string().min(1).max(160),
  /**
   * The identity provider's id for the organization (Clerk's `org_...`): its
   * unique identifier a person reads, in smaller grey type beside the name.
   * Null for an organization the platform created itself - a bakery house.
   */
  externalId: z.string().max(160).nullable(),
  type: OrganizationType,
  payoutVerification: PayoutVerificationState,
  /** PRD WEB-014 - default 5 for self-serve organizations. */
  qrGenerationCap: z.int().nonnegative(),
  qrGenerationUsed: z.int().nonnegative(),
  suspended: z.boolean(),
  /**
   * The menu types this organization may use (OPS-013), in canonical order and
   * never empty. For a property owner it is the set operations ticked: one
   * type means every apartment is on it and the owner is offered no control,
   * several mean the owner assigns one per apartment. A bakery holds exactly
   * one, its own immutable mode. The rules are `projections/storefront-modes`.
   */
  allowedStorefrontModes: AllowedStorefrontModes,
  /** The type a new apartment takes when its creator names none: the first allowed. */
  defaultStorefrontMode: StorefrontMode,
  createdAt: Instant,
});
export type OrganizationSummary = z.infer<typeof OrganizationSummary>;

// ---------------------------------------------------------------------------
// Incidents and support
// ---------------------------------------------------------------------------

export const IssueReport = z.object({
  id: Uuid,
  orderId: Uuid,
  lineIds: z.array(Uuid),
  photoUrl: ImageUrl.nullable(),
  text: z.string().max(4000),
  cause: IncidentCause.nullable(),
  status: z.enum(['OPEN', 'IN_PROGRESS', 'RESOLVED']),
  createdAt: Instant,
});
export type IssueReport = z.infer<typeof IssueReport>;
